import { CURRENT_PAGE_DEFAULT } from 'constants/config';
import CameraDisplayer from 'pages/components/CameraDisplayer';
import PropTypes from 'prop-types';
import React from 'react';
import '../SplitPage.css';

const propTypes = {
    curPage: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.object),
    itemNumPerPage: PropTypes.number,
};

const defaultProps = {
    curPage: CURRENT_PAGE_DEFAULT,
    items: [],
    itemNumPerPage: 2,
};

const CameraList = ({ curPage, items, itemNumPerPage }) => {
    const [loadedRows, setLoadedRows] = React.useState(1);
    const [videoLoadStates, setVideoLoadStates] = React.useState(new Set());
    const [isInitialLoad, setIsInitialLoad] = React.useState(true);

    const numColumns = React.useMemo(() => Math.round(Math.sqrt(itemNumPerPage)), [itemNumPerPage]);

    const pagedItems = React.useMemo(() => items.slice(
        (curPage - 1) * itemNumPerPage,
        curPage * itemNumPerPage
    ), [items, curPage, itemNumPerPage]);

    const rows = React.useMemo(() => {
        const result = [];
        // Special case: for 4 or 9 items per page, put all items in one row
        if ([4, 9].includes(itemNumPerPage)) {
            if (pagedItems.length > 0) {
                result.push(pagedItems);
            }
        } else {
            for (let i = 0; i < pagedItems.length; i += numColumns) {
                result.push(pagedItems.slice(i, i + numColumns));
            }
        }
        return result;
    }, [pagedItems, numColumns, itemNumPerPage]);

    // Reset load states khi chuyển page
    React.useEffect(() => {
        setLoadedRows(1);
        setVideoLoadStates(new Set());
        setIsInitialLoad(true);
    }, [curPage]);

    // Kiểm tra và load hàng tiếp theo
    const checkAndLoadNextRow = React.useCallback((currentRowIndex) => {
        if (!isInitialLoad) return;

        const currentRow = rows[currentRowIndex];
        if (!currentRow) return;

        // Lấy tất cả video items trong hàng hiện tại
        const videoItemsInRow = currentRow
            .map((item, colIndex) => ({
                item,
                index: currentRowIndex * numColumns + colIndex,
                isVideo: item.data.typeDisplayCamera === "VIDEO"
            }))
            .filter(({ isVideo }) => isVideo);

        // Nếu không có video nào trong hàng này, load hàng tiếp theo ngay
        if (videoItemsInRow.length === 0) {
            setTimeout(() => {
                setLoadedRows(prev => Math.min(prev + 1, rows.length));
            }, 50);
            return;
        }

        // Kiểm tra tất cả video trong hàng đã load xong chưa
        const allVideosLoaded = videoItemsInRow.every(({ index }) =>
            videoLoadStates.has(index)
        );

        if (allVideosLoaded && currentRowIndex + 1 < rows.length) {
            setTimeout(() => {
                setLoadedRows(prev => Math.min(prev + 1, rows.length));
            }, 100); // Delay nhỏ để tránh load quá nhanh
        }
    }, [rows, numColumns, videoLoadStates, isInitialLoad]);

    React.useEffect(() => {
        if (loadedRows > 0) {
            checkAndLoadNextRow(loadedRows - 1);
        }
    }, [videoLoadStates, loadedRows, checkAndLoadNextRow]);

    const handleVideoLoaded = React.useCallback((rowIndex, colIndex) => {
        const videoIndex = rowIndex * numColumns + colIndex;
        setVideoLoadStates(prev => new Set([...prev, videoIndex]));
    }, [numColumns]);

    const renderItem = (sensor, rowIndex, colIndex) => {
        const cameraNumber = sensor.cameraNumber || 1;
        const sensorData = sensor.data;
        const isVideo = sensorData.typeDisplayCamera === "VIDEO";

        // Chỉ active video nếu:
        // 1. Không phải initial load (đã load all) HOẶC
        // 2. Đang trong initial load và hàng này đã được load
        const shouldLoadVideo = !isInitialLoad || rowIndex < loadedRows;

        return (
            <CameraDisplayer
                key={`${sensor.id}-${cameraNumber}`}
                targetSensor={sensor}
                cameraNumber={cameraNumber}
                isDisplayVideo={isVideo}
                isImageAnalysis={false}
                isInList
                style={{ width: 'unset' }}
                imageProps={{
                    key: cameraNumber === 1 ? sensorData.keyNewestImage : sensorData.keyNewestImage2,
                    src: cameraNumber === 1 ? sensorData.image1Src : sensorData.image2Src,
                }}
                videoProps={{
                    lazy: true,
                    isActive: isVideo ? shouldLoadVideo : true,
                    onVideoLoaded: () => {
                        if (isVideo && shouldLoadVideo) {
                            handleVideoLoaded(rowIndex, colIndex);
                        }
                    },
                }}
                onClick={(_, { cameraNumber }) => {
                    const location = `http://${encodeURIComponent(sensorData.username)}:${encodeURIComponent(sensorData.password)}@${sensorData.ipAddress}/axis-cgi/jpg/image.cgi?camera=${cameraNumber}`;
                    window.open(location, "_blank");
                  }}
            />
        );
    };

    return (
        <>
            {rows.map((row, rowIndex) =>
                <React.Fragment key={`${curPage}-${rowIndex}-${itemNumPerPage}`}>
                    {row.map((item, colIndex) =>
                        renderItem(item, rowIndex, colIndex)
                    )}
                </React.Fragment>
            )}
        </>
    );
};


CameraList.propTypes = propTypes;
CameraList.defaultProps = defaultProps;

export default CameraList;
